import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';
import { injectIntl, FormattedMessage } from 'react-intl';
import BookingDetailsActionCreator from 'action_creators/booking_details';
import Dropdown from 'views/ui/form_elements/dropdown';
import Calendar from './calendar';
import { formatDate } from './Helpers';

class Dates extends React.PureComponent {
  static displayName = 'BookingRequest-Dates';

  static propTypes = {
    tourId: PropTypes.number.isRequired,
    availableDates: PropTypes.object.isRequired,
    initialDatesCount: PropTypes.number.isRequired,
    isLoadingAvailableDates: PropTypes.bool,
    datepickerType: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.string,
    errors: PropTypes.array,
    showDates: PropTypes.bool,
    disabled: PropTypes.bool,
    onMount: PropTypes.func,
    hasTicketCapacityEnabled: PropTypes.bool,
  };

  static defaultProps = {
    value: null,
    disabled: false,
    hasTicketCapacityEnabled: false,
    errors: [],
    onMount() {},
  };

  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    if (this.props.showDates && !this.props.multiple_locations) {
      this._showDates();
      BookingDetailsActionCreator.datesShown();
    }

    this._selectDate(false);
    this.props.onMount(this._value());
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.showDates && this.props.showDates !== prevProps.showDates) {
      this._showDates();
      BookingDetailsActionCreator.datesShown();
    }

    // Auto-select date if only 1 available
    if (this.props.availableDates) {
      if (
        !(prevProps.value || this.props.value) &&
        Object.keys(this.props.availableDates).length === 1
      ) {
        this._selectDate();
      } else if (
        this.props.value &&
        !this.props.availableDates[this.props.value]
      ) {
        const newDate = this._hasMultipleDates()
          ? undefined
          : this._firstAvailableDate();
        this.props.onChange(this.props.name, newDate);
      }
    }

    if (this.props.errors.length > 0) {
      if (this.refs.datepicker !== undefined && this._canShowDates()) {
        this._showDates();
      } else {
        $(this.refs.label).transition({
          animation: 'shake',
          displayType: 'block',
          duration: '600ms',
        });
      }
    }
  }

  _canShowDates = () => {
    if (this._datePickerType === 'calendar') {
      return !this.refs.datepicker.isVisible();
    }
    return false;
  };

  _availableDates = () => {
    return Object.keys(this.props.availableDates).sort();
  };

  _showDates = () => {
    if (this.refs.datepicker !== undefined) {
      if (this._datePickerType() === 'calendar') {
        this.refs.datepicker.showCalendar();
      } else {
        this.refs.datepicker.showDropdown();
      }
    }
  };

  _value = () => {
    return (
      this.props.value ||
      (!this._hasMultipleDates() ? this._firstAvailableDate() : null)
    );
  };

  _selectDate = (triggerOnChange = true) => {
    if (!this._value()) {
      return;
    }
    if (this.props.value !== this._value() && triggerOnChange) {
      this.props.onChange(this.props.name, this._value());
    }
  };

  _firstAvailableDate = () => {
    return this._availableDates()[0];
  };

  _dateDropdownOptions = () => {
    return _.reduce(
      this._availableDates(),
      (options, availableDate) => {
        options[availableDate] = formatDate(this.props.intl, availableDate);
        return options;
      },
      {}
    );
  };

  _datepickerProps = () => {
    const {
      value,
      name,
      intl,
      onChange,
      disabled,
      weekStartDay,
      hasTicketCapacityEnabled,
    } = this.props;
    return {
      value,
      name,
      placeholder: intl.formatMessage({
        id: 'SELECT',
        defaultMessage: 'Select',
      }),
      onChange,
      ref: 'datepicker',
      disabled,
      weekStartDay,
      hasTicketCapacityEnabled,
    };
  };

  _dropdownProps = () => {
    return _.extend(
      {
        availableDates: this._availableDates(),
        options: this._dateDropdownOptions(),
        icon: <i className="icon af-triangle-down-2" />,
        theme: 'booking-request',
        'data-testid': 'date-dropdown',
      },
      this._datepickerProps()
    );
  };

  _calendarProps = () => {
    return _.extend(
      {
        multiple_locations: this.props.multiple_locations,
        tourId: this.props.tourId,
        availableDates: this.props.availableDates,
        isLoadingAvailableDates: this.props.isLoadingAvailableDates,
        valueFormat: 'YYYY-MM-DD',
        icon: <i className="icon af-calendar" />,
        location: this.props.location,
        ariaLabelledBy: 'date-selection',
      },
      this._datepickerProps()
    );
  };

  _hasMultipleDates = () => {
    return this._availableDates().length > 1;
  };

  _shouldRenderCalendar = () => {
    return this.props.datepickerType === 'calendar';
  };

  _renderSingleDate = () => {
    const singleDate = this._firstAvailableDate();

    return singleDate ? (
      <div
        className={`TourPage-BookingDetails-Dates-single ${
          this.props.graySingleDate ? 'gray' : ''
        }`}
        data-testid="booking-date"
      >
        {formatDate(this.props.intl, singleDate)}
      </div>
    ) : null;
  };

  _datePickerType = () => {
    if (
      !this.props.availableDates ||
      !this._availableDates() ||
      this._availableDates().length === 0
    ) {
      return this.props.datepickerType;
    }

    if (this._availableDates().length === 1) {
      return 'single-date';
    } else if (this._availableDates().length < 8) {
      return 'dropdown';
    }

    return 'calendar';
  };

  _renderDates = () => {
    let view = null;

    switch (this._datePickerType()) {
      case 'calendar':
        view = <Calendar {...this._calendarProps()} />;
        break;
      case 'dropdown':
        view = <Dropdown {...this._dropdownProps()} />;
        break;
      case 'single-date':
        view = this._renderSingleDate();
        break;
      default:
        view = null;
    }

    return view;
  };

  render() {
    // Ultra magical hack to mitigate shaking effect weirdness!
    const inlineStyles = {
      display: 'block !important',
    };

    return (
      <div data-testid="date-field" className="field" id="date-selection">
        <label
          className="date-label show"
          ref="label"
          aria-label="Select date"
          style={inlineStyles}
        >
          <FormattedMessage id="DATE" defaultMessage="Date" />
        </label>
        {this._renderDates()}
      </div>
    );
  }
}

export default injectIntl(Dates);
